import { GameStatus, SlateStatus } from '@betterpool/api-types/games-service';
import type { ContestSettings } from '@betterpool/api-types/contests-service';

import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';
import type { UserEntry } from '~/domains/contest/domains/services/interfaces/user-entry.interface';
import type { TeamPicks } from '~/domains/contest/domains/services/interfaces/team-picks.interface';
import type { LeaderboardEntryScores } from '~/domains/contest/domains/services/interfaces/leaderboard-entry-scores';
import type { Picksheet } from '~/domains/contest/domains/services/interfaces/picksheet.interface';

import { findDefaultSlate } from '../../../../../../games/services/find-default-slate.utils';
import type { SingleSlate } from '../types/single.slate';

import getSingleSlate from './getSingleSlate';
import getFootballSlateLabel from './getFootballSlateLabel';

type GetSlatesProps = {
  selectedSlateId?: string;
  picksheet?: Picksheet;
  slates: ContestSlate[];
  settings: ContestSettings;
  leagueAlias: string;
  currentUserEntry: UserEntry;
  currentTeamPick: TeamPicks['entryId'];
  // When user is not logged or missing KYC or has not enough funds
  isUserRestricted?: boolean;
  entryScores?: LeaderboardEntryScores;
};

const ELIMINATED = 0;

export const getSingleSlates = ({
  selectedSlateId,
  settings,
  picksheet,
  leagueAlias,
  slates,
  currentUserEntry,
  currentTeamPick = {},
  isUserRestricted,
  entryScores,
}: GetSlatesProps): {
  singleSlates: SingleSlate[];
  currentSingleSlate: SingleSlate;
  liveSingleSlate: SingleSlate;
} => {
  const expectedPicksCount = settings?.expectedPicksCount;

  const liveSlateId = findDefaultSlate(slates)?.id;
  // TODO: perhaps storing this info in URL could be helpful
  // EXAMPLE: team-survivor/pick?slate=:uuid
  const currentSlateId = selectedSlateId ?? liveSlateId;

  /**
   * Contest settings contains expectedPicksCount which tells us how many picks per slate we can make
   * but it also contains stepUp object which can override expectedPicksCount
   * by expectedPicksCount in stepUp for specific slate.
   */
  const overrideExpectedPicksCount = (slateId: string) =>
    settings?.stepUp?.slates.find(({ slateId: id }) => id === slateId);

  const getPicks = (slateId: string, alias: string) =>
    currentTeamPick.picks?.[slateId]
      ?.map(({ teamId, gameId, winning, teamName, teamAlias, isBestPick }) => {
        const gameIndex = slates
          ?.find(({ id }) => slateId === id)
          ?.games?.map(({ id }) => id)
          ?.indexOf(gameId);

        return {
          gameId,
          gameIndex,
          teamId,
          label: `G${(gameIndex ?? 0) + 1}`,
          name: teamName,
          teamAlias,
          logo: getTeamOrPlayerImageUrl(alias, teamId),
          isWinning: winning,
          isBestPick,
        };
      })
      .sort((a, b) => a.gameIndex - b.gameIndex);

  const userEntryIsEliminated = currentUserEntry?.livesRemaining === ELIMINATED;

  let currentEliminatedSlateIndex = Number(slates?.length);
  const singleSlates: SingleSlate[] = slates?.map((slate, i) => {
    const entryScoresForSlate = entryScores?.slates[slate.id];
    const isLoser = currentTeamPick.picks?.[slate.id]?.some(
      ({ winning }) => typeof winning === 'boolean' && !winning
    );

    const isWinner =
      currentTeamPick.picks?.[slate.id]?.length > 0
        ? currentTeamPick.picks?.[slate.id]?.every(
            ({ winning }) => typeof winning === 'boolean' && winning
          )
        : false;

    const isSlateEliminated = currentTeamPick.picks?.[slate.id]?.some(
      ({ winning }) =>
        typeof winning === 'boolean' &&
        !winning &&
        userEntryIsEliminated &&
        (slate.status === SlateStatus.FINISHED || slate.status === SlateStatus.SETTLED)
    );

    if (isSlateEliminated) {
      currentEliminatedSlateIndex = i;
    }

    const isLive = slate.status === SlateStatus.LIVE || slate.status === SlateStatus.IN_PROGRESS;
    const isEliminated = currentEliminatedSlateIndex < i;

    const expectedPicksCountNumber =
      overrideExpectedPicksCount(slate.id)?.expectedPicksCount || expectedPicksCount;

    const getIsSlateLocked = () => {
      const pickDeadlineType = settings?.pickDeadlineType;
      if (pickDeadlineType === 'game') {
        return slate.games?.every((game) => game.status !== GameStatus.SCHEDULED);
      }
      return slate.late_swap_deadline
        ? slate.late_swap_deadline < new Date()
        : slate.status !== SlateStatus.SCHEDULED;
    };

    return {
      endDate: new Date(slate.end_date),
      expectedPicksCount: expectedPicksCountNumber,
      id: slate.id,
      isActive: slate.status === SlateStatus.LIVE && !isEliminated,
      isEliminated,
      isGenerated: !slate.picksheetAvailableAt,
      isLive,
      isLocked: !picksheet?.canMakePicks, // Potentionally deprecate in favor of isSlateLocked
      isLoser,
      isOver: !isLive && slate.status !== SlateStatus.SCHEDULED, // Can't be trusted because of late swap...
      isSelected: currentSlateId === slate.id,
      isSlateLocked: getIsSlateLocked(),
      isUnavailable: isUserRestricted && i > 0,
      isBestPickEnabled: settings?.bestPicks?.[slate.id],
      isWinner,
      label: getFootballSlateLabel(slate.name),
      name: slate.name,
      picks: getPicks(slate.id, leagueAlias),
      points: entryScoresForSlate?.score,
      startDate: new Date(slate.start_date),
      status: slate.status,
      weight: slate.weight,
      gamesCount: slate.games?.length,
    };
  });

  return {
    singleSlates,
    currentSingleSlate: getSingleSlate<SingleSlate[]>(currentSlateId, singleSlates),
    liveSingleSlate: getSingleSlate<SingleSlate[]>(currentSlateId, singleSlates),
  };
};
